import { ArrowLeftRight, Trash2, X } from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/**
 * Shown when a crop is dropped on a cell already occupied by another crop.
 *
 * Two flows:
 *   - SWAP (sourceCrop has a position): swap source ↔ target via batch endpoint.
 *   - DISPLACE (sourceCrop is from palette): un-place the target so the source
 *     can take its cell. We don't auto-confirm because it's a destructive move.
 */
export function CollisionDialog({ collision, onSwap, onDisplace, onCancel }) {
    if (!collision) return null;
    const { sourceCrop, targetCrop } = collision;
    const canSwap = !!sourceCrop?.position;

    return (
        <Dialog open onOpenChange={(o) => !o && onCancel()}>
            <DialogContent
                className="bg-[var(--vm-card)] border-[var(--vm-border)] max-w-md"
                data-testid="collision-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="font-serif-display text-2xl flex items-center gap-2">
                        That cell is taken
                    </DialogTitle>
                    <DialogDescription className="text-sm text-[var(--vm-text-secondary)]">
                        <span className="font-medium text-[var(--vm-text)]">{targetCrop.crop_name}</span>
                        {" "}is already growing in row {targetCrop.position.row}, column {targetCrop.position.col}.
                        {canSwap
                            ? " You can swap them, send the existing crop back to the palette, or cancel."
                            : " You can send the existing crop back to the palette so this one takes its place, or cancel."}
                    </DialogDescription>
                </DialogHeader>

                <DialogFooter className="flex-col sm:flex-row gap-2">
                    <Button
                        variant="ghost"
                        onClick={onCancel}
                        data-testid="collision-cancel"
                        className="rounded-full"
                    >
                        <X className="w-4 h-4 mr-2" /> Cancel
                    </Button>
                    {canSwap && (
                        <Button
                            onClick={onSwap}
                            data-testid="collision-swap"
                            className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]"
                        >
                            <ArrowLeftRight className="w-4 h-4 mr-2" /> Swap places
                        </Button>
                    )}
                    <Button
                        onClick={onDisplace}
                        data-testid="collision-displace"
                        className={`rounded-full ${canSwap
                            ? "bg-[var(--vm-secondary)] hover:bg-[var(--vm-secondary-hover)] text-[var(--vm-bg)]"
                            : "bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]"}`}
                    >
                        <Trash2 className="w-4 h-4 mr-2" /> Move existing to palette
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
